import type { CapacityProjection, CapacityProjectionInputs } from 'services/api/getMangagerCenterData';

const formatDecimal = (value: number, maximumFractionDigits = 1): string =>
    value.toLocaleString('en-US', { maximumFractionDigits });

/**
 * Why there is no number to show. The backend zeroes both figures when `available` is false,
 * so a bare 0 would read as "we project no calls today" rather than "we cannot project".
 * active_workflows is checked first: with nothing dialling, the averages are moot.
 */
export const capacityUnavailableReason = (projection?: CapacityProjection): string => {
    if (!projection) {
        return 'Capacity projection is unavailable right now. It returns on the next refresh.';
    }

    const { active_workflows, sample_calls, window_days } = projection.inputs;

    if (active_workflows === 0) {
        return 'No active workflows, so nothing is dialling today and there is no capacity to project. Start a workflow to see a forecast.';
    }

    if (sample_calls === 0) {
        return `No connected calls in the last ${window_days} days, so there is no average handle time to project from. The forecast returns once calls complete.`;
    }

    return 'Not enough call data to project capacity yet.';
};

/**
 * What the average handle time actually measures. Surfaced rather than hidden because the
 * projection divides by it: talk time alone means the forecast does not account for wrap time,
 * so the number runs optimistic.
 */
export const formatHandleTimeBasis = (basis: string): string =>
    basis === 'talk_time_only' ? 'talk time only, excludes wrap time' : basis.replace(/_/g, ' ');

/** Operating hours read off a workflow, or the 8h fallback the backend assumed. */
export const formatOperatingWindow = (inputs: CapacityProjectionInputs): string => {
    const hours = `${formatDecimal(inputs.operating_minutes_per_day / 60)}h/day`;
    return inputs.operating_hours_source === 'default' ? `${hours} (assumed default)` : hours;
};

export const formatHandleTime = (minutes: number): string => `${formatDecimal(minutes)} min`;

export const formatContactRate = (pct: number): string => `${formatDecimal(pct)}%`;

/** The window the averages come from, so the manager can judge how much to trust them. */
export const formatSampleBasis = (inputs: CapacityProjectionInputs): string => {
    const calls = inputs.sample_calls === 1 ? 'call' : 'calls';
    return `${inputs.sample_calls.toLocaleString()} ${calls} over ${inputs.window_days} days`;
};
